import {
  JobSettingSeminar,
  JobSettingSeminarScheduleItem
} from "@agent-ds/shared/interfaces/job-setting-seminar";
import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges
} from "@angular/core";
import { JobApiService, DialogService } from "@agent-ds/shared/services";
import { ConfirmDialogConfig } from "@agent-ds/shared/components/confirm-dialog/confirm-dialog-config";
import { ConfirmDialogComponent } from "@agent-ds/shared/components/confirm-dialog/confirm-dialog.component";
import { RowMeta } from "@agent-ds/shared/models";
import * as moment from "moment";

type RowErrorField = "date" | "capacity";

interface RowError {
  field: RowErrorField;
  message: string;
}

interface BulkParsedEntry {
  eventDateAt: string;
  eventDateTimeFrom: string;
  eventDateTimeTo: string;
}

interface BulkParseResult {
  entries: BulkParsedEntry[];
  errors: string[];
}

@Component({
  selector: "ag-job-seminar-tab",
  templateUrl: "./job-seminar-tab.component.html",
  styleUrls: ["./job-seminar-tab.component.scss"]
})
export class JobSeminarTabComponent implements OnInit, OnChanges {
  @Input() jobId: number;

  // 今日以降しか選べないようにするための minDate (YYYY-MM-DD, 日本時間)
  minDate: string;

  headMeta = {
    groups: [
      {
        title: "セミナー設定",
        class: "form__group--green-title-border",
        rows: [
          {
            title: "日程公開ステータス",
            fields: [
              {
                name: "seminarStatus",
                type: "dropdown",
                options: [
                  { label: "NULL", value: null },
                  { label: "営業書類結果待ち", value: 40 },
                  { label: "企業書類提出待ち", value: 50 },
                  { label: "企業書類結果待ち", value: 60 }
                ],
                labelField: "label",
                valueField: "value"
              }
            ]
          },
          {
            title: "予約期限",
            fields: [
              {
                name: "reservationDeadlineDays",
                type: "dropdown",
                options: [
                  { label: "前日", value: 1 },
                  { label: "2日前", value: 2 },
                  { label: "3日前", value: 3 },
                  { label: "4日前", value: 4 },
                  { label: "5日前", value: 5 },
                  { label: "6日前", value: 6 },
                  { label: "1週間前", value: 7 }
                ],
                labelField: "label",
                valueField: "value"
              },
              {
                name: "reservationDeadlineTime",
                type: "time"
              }
            ]
          },
          {
            title: "動員枠一斉反映",
            name: "bulkCapacityValue",
            fields: [
              {
                name: "bulkCapacityValue",
                type: "number"
              },
              {
                name: "applyBulkCapacity",
                type: "label",
                actions: [
                  {
                    title: "一斉反映する",
                    type: "RUNNABLE",
                    runnable: () => {
                      this.applyBulkCapacity();
                    }
                  }
                ]
              }
            ]
          },
          {
            title: "即確定フラグ",
            fields: [
              {
                name: "isAutoConfirm",
                type: "checkbox",
                label: "即確定する"
              }
            ]
          },
          {
            title: "基本情報必須フラグ",
            fields: [
              {
                name: "isBasicInfoRequired",
                type: "checkbox",
                label: "基本情報必須"
              }
            ]
          },
          {
            title: "履歴書必須フラグ",
            fields: [
              {
                name: "isResumeRequired",
                type: "checkbox",
                label: "履歴書必須"
              }
            ]
          }
        ]
      }
    ]
  };

  jobSettingSeminar: JobSettingSeminar | null = null;

  dynamicFormModel = this.defaultSetting();

  bulkScheduleInput = "";
  bulkTextErrors: string[] = [];

  schedule: JobSettingSeminarScheduleItem[] = [];
  rowErrors: RowError[][] = [];
  oldSchedule: JobSettingSeminarScheduleItem[] = [];
  isRefreshing = false;

  constructor(
    private readonly jobApiService: JobApiService,
    private readonly dialogService: DialogService
  ) {}

  // 削除確認ダイアログ
  confirmReset(index: number, reservationCount: number): void {
    // 予約数が0件の場合はそのまま削除
    if (reservationCount === 0) {
      this.onResetSetting(index);
      return;
    }

    // ConfirmDialog の設定を作成して開く
    const dialogConfig: ConfirmDialogConfig = new ConfirmDialogConfig();
    dialogConfig.title = "確認";
    dialogConfig.messages = ["現在の予約数が 1 以上ですが、削除しますか？"];
    dialogConfig.buttons = { yes: "はい", no: "いいえ" };

    const dialogRef = this.dialogService.open(
      ConfirmDialogComponent as any,
      dialogConfig
    );
    dialogRef.afterClosed.subscribe(result => {
      if (result) {
        this.onResetSetting(index);
      }
    });
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.jobId != null &&
      changes["jobId"].currentValue !== changes["jobId"].previousValue
    ) {
      // コンポーネント表示時に日本時間の今日の日付を minDate に設定
      this.minDate = moment()
        .utcOffset(9)
        .format("YYYY-MM-DD");

      this.load();
    }
  }

  refresh(): void {
    if (!this.jobId || this.isRefreshing) {
      return;
    }

    this.load();
  }

  private load(): void {
    this.jobApiService.getJobSettingSeminar(this.jobId).subscribe(setting => {
      this.schedule = Array.from({ length: 15 }, () => this.defaultData());
      this.resetRowErrors();
      this.bulkScheduleInput = "";
      if (setting) {
        this.jobSettingSeminar = setting;
        this.ensureInitialSettings();
      } else {
        this.jobSettingSeminar = null;
        this.dynamicFormModel = this.defaultSetting();
      }
    });
  }

  private defaultSetting() {
    return {
      seminarStatus: null,
      reservationDeadlineDays: 1,
      reservationDeadlineTime: "23:59",
      isAutoConfirm: false,
      isBasicInfoRequired: false,
      isResumeRequired: false,
      bulkCapacityValue: null,
      bulkDateTime: null,
      bulkEventDate: "",
      bulkEventTimeFrom: "",
      bulkEventTimeTo: "",
      bulkEventError: ""
    };
  }

  private defaultData() {
    return {
      eventDateAt: "",
      eventDateTimeFrom: "",
      eventDateTimeTo: "",
      capacity: 0,
      reservationCount: 0
    };
  }

  // 行ごとのエラー状態を配列で初期化する
  private resetRowErrors(): void {
    this.rowErrors = Array.from({ length: this.schedule.length }, () => []);
  }

  private ensureInitialSettings(): void {
    this.dynamicFormModel = {
      ...this.dynamicFormModel,
      seminarStatus: this.jobSettingSeminar.publishStatus,
      reservationDeadlineDays: this.jobSettingSeminar.reservationDeadlineDays,
      reservationDeadlineTime: moment(
        this.jobSettingSeminar.reservationDeadlineTime,
        "HH:mm:ss"
      ).format("HH:mm"),
      isAutoConfirm: Boolean(this.jobSettingSeminar.isAutoConfirm),
      isBasicInfoRequired: Boolean(this.jobSettingSeminar.isBasicInfoRequired),
      isResumeRequired: Boolean(this.jobSettingSeminar.isResumeRequired)
    };

    this.jobSettingSeminar.schedule.map((item, index) => {
      this.schedule[index] = {
        id: item.id || undefined,
        eventDateAt: moment(item.startAt).format("YYYY-MM-DD"),
        eventDateTimeFrom: moment(item.startAt).format("HH:mm"),
        eventDateTimeTo: moment(item.endAt).format("HH:mm"),
        capacity: item.capacity,
        reservationCount: item.reservationCount || 0
      };
    });

    this.oldSchedule = JSON.parse(JSON.stringify(this.schedule));
  }

  // 動員枠の一斉更新
  private applyBulkCapacity(): void {
    const value = this.dynamicFormModel.bulkCapacityValue;
    if (value != null) {
      for (let i = 0; i < this.schedule.length; i++) {
        this.schedule[i].capacity = value;
      }
    }
  }

  // テキスト一括入力の反映
  applyBulkTextToSchedule(): void {
    this.bulkTextErrors = [];
    const { entries, errors } = this.parseBulkTextInput(this.bulkScheduleInput);
    if (errors.length) {
      this.bulkTextErrors = errors;
      return;
    }

    if (entries.length === 0) {
      this.bulkTextErrors = ["1件以上の開催日時をテキストで入力してください。"];
      return;
    }

    const currentLength =
      this.jobSettingSeminar && this.jobSettingSeminar.schedule
        ? this.jobSettingSeminar.schedule.length
        : 0;

    if (entries.length + currentLength > this.schedule.length) {
      this.bulkTextErrors = [
        `現在の設定可能な日程数を超えています。最大であと ${this.schedule
          .length - currentLength} 件まで追加可能です。`
      ];
      return;
    }

    for (let i = 0; i < entries.length; i++) {
      const rowIndex = currentLength + i;
      this.schedule[rowIndex] = {
        ...this.schedule[rowIndex],
        ...entries[i],
        reservationCount: 0
      };
    }

    this.resetRowErrors();
  }

  // 日程のテキスト入力のパース
  private parseBulkTextInput(text: string): BulkParseResult {
    const errors: string[] = [];
    const entries: BulkParsedEntry[] = [];
    const lineRegex = /^(\d{4})\/(\d{2})\/(\d{2})\s+(\d{2}:\d{2})\s*~\s*(\d{2}:\d{2})$/;
    const jstToday = moment()
      .utcOffset(9)
      .startOf("day");

    text.split(/\r?\n/).forEach((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        return;
      }

      const match = trimmedLine.match(lineRegex);
      if (!match) {
        errors.push(
          `行${index + 1}: YYYY/MM/DD HH:mm~HH:mm 形式で記述してください。`
        );
        return;
      }

      const trimmed = {
        date: `${match[1]}-${match[2]}-${match[3]}`,
        from: match[4],
        to: match[5]
      };

      const range = this.getRangeFromTrimmed(trimmed);
      if (!range) {
        errors.push(`行${index + 1}: 日時の形式が正しくありません。`);
        return;
      }

      if (!range.start.isBefore(range.end)) {
        errors.push(`行${index + 1}: 終了時刻は開始時刻より後にしてください。`);
        return;
      }

      const entryDate = moment(trimmed.date, "YYYY-MM-DD", true)
        .utcOffset(9)
        .startOf("day");
      if (!entryDate.isValid() || entryDate.isSameOrBefore(jstToday)) {
        errors.push(`行${index + 1}: 当日以前の日程は登録できません。`);
        return;
      }

      entries.push({
        eventDateAt: trimmed.date,
        eventDateTimeFrom: trimmed.from,
        eventDateTimeTo: trimmed.to
      });
    });

    return { entries, errors };
  }

  // 日程変更時のダイアログ表示
  onConfirmChange(newValue: any, field: string, index: number): void {
    const value = newValue.target.value;
    if (this.schedule[index].reservationCount === 0) {
      this.schedule[index][field] = value;
    } else {
      // ConfirmDialog の設定を作成して開く
      const dialogConfig: ConfirmDialogConfig = new ConfirmDialogConfig();
      dialogConfig.title = "確認";
      dialogConfig.messages = ["現在の予約数が 1 以上ですが、変更しますか？"];
      dialogConfig.buttons = { yes: "はい", no: "いいえ" };

      const dialogRef = this.dialogService.open(
        ConfirmDialogComponent as any,
        dialogConfig
      );
      dialogRef.afterClosed.subscribe(result => {
        if (result) {
          this.schedule[index][field] = value;
        } else {
          this.schedule[index][field] = this.oldSchedule[index][field];
        }
      });
    }
  }

  // 動員枠変更時のダイアログ表示
  onCapacityChange(newValue: any, index: number): void {
    if (newValue.target.value === "" || newValue.target.value === null) {
      return;
    }

    const number = Number(newValue.target.value);
    const value = number >= 0 ? number : null;
    if (value === null) {
      return;
    }

    if (this.schedule[index].reservationCount > value) {
      // ConfirmDialog の設定を作成して開く
      const dialogConfig: ConfirmDialogConfig = new ConfirmDialogConfig();
      dialogConfig.title = "確認";
      dialogConfig.messages = [
        "動員枠が現在数を下回ってしまいますが、変更しますか？"
      ];
      dialogConfig.buttons = { yes: "はい", no: "いいえ" };

      const dialogRef = this.dialogService.open(
        ConfirmDialogComponent as any,
        dialogConfig
      );
      dialogRef.afterClosed.subscribe(result => {
        if (result) {
          this.schedule[index].capacity = value;
        } else {
          this.schedule[index].capacity = this.oldSchedule[index].capacity;
        }
      });
    } else {
      this.schedule[index].capacity = value;
    }
  }

  // 削除
  onResetSetting(index: number): void {
    this.schedule[index] = this.defaultData();
    this.rowErrors[index] = [];
  }

  // 全てが空欄のものは除去
  private removeEmpty(setting: JobSettingSeminarScheduleItem): boolean {
    return Boolean(
      setting.eventDateAt &&
        setting.eventDateTimeFrom &&
        setting.eventDateTimeTo &&
        (Number(setting.capacity) || 0) > 0
    );
  }

  // 指定行・項目に紐づいたエラー配列を返す
  getRowFieldErrors(index: number, field: RowErrorField): RowError[] {
    return (this.rowErrors[index] || []).filter(error => error.field === field);
  }

  // 各入力行を検証して、その行のエラー一覧を返す
  private getRowErrors(setting: JobSettingSeminarScheduleItem): RowError[] {
    const trimmed = this.getTrimmedValues(setting);
    if (!this.hasAnyInput(setting, trimmed)) {
      return [];
    }
    if (this.isCapacityOnly(setting, trimmed)) {
      return [];
    }

    const errors: RowError[] = [];
    const hasFullDate =
      trimmed.date !== "" && trimmed.from !== "" && trimmed.to !== "";

    if (!hasFullDate) {
      errors.push({
        field: "date",
        message: "日時・開始・終了時刻をすべて入力してください。"
      });
    }

    const capacity = Number(setting.capacity) || 0;
    if (capacity < 1) {
      errors.push({
        field: "capacity",
        message: "動員枠は1以上で入力してください。"
      });
    }

    if (hasFullDate) {
      const entryDate = moment(trimmed.date, "YYYY-MM-DD", true)
        .utcOffset(9)
        .startOf("day");
      const jstToday = moment()
        .utcOffset(9)
        .startOf("day");

      if (!entryDate.isValid() || entryDate.isBefore(jstToday)) {
        errors.push({
          field: "date",
          message: "過去の日付は入力できません。"
        });
      }

      const range = this.getRangeFromTrimmed(trimmed);
      if (!range) {
        errors.push({
          field: "date",
          message: "日時の形式が正しくありません。"
        });
      } else if (!range.start.isBefore(range.end)) {
        errors.push({
          field: "date",
          message: "終了時刻は開始時刻より後にしてください。"
        });
      }
    }

    return errors;
  }

  // 日付または時刻・容量のいずれかが入力されているか確認する
  private hasAnyInput(
    setting: JobSettingSeminarScheduleItem,
    trimmed?: { date: string; from: string; to: string }
  ): boolean {
    const values = trimmed || this.getTrimmedValues(setting);
    const capacity = Number(setting.capacity) || 0;
    return Boolean(values.date || values.from || values.to) || capacity > 0;
  }

  // 容量のみが入力されているか確認する
  private isCapacityOnly(
    setting: JobSettingSeminarScheduleItem,
    trimmed?: { date: string; from: string; to: string }
  ): boolean {
    const values = trimmed || this.getTrimmedValues(setting);
    const capacity = Number(setting.capacity) || 0;
    const hasDateInput = Boolean(values.date || values.from || values.to);
    return capacity > 0 && !hasDateInput;
  }

  // 入力値をトリムして一貫したフォーマットで返す
  private getTrimmedValues(setting: JobSettingSeminarScheduleItem) {
    return {
      date: (setting.eventDateAt || "").toString().trim(),
      from: (setting.eventDateTimeFrom || "").toString().trim(),
      to: (setting.eventDateTimeTo || "").toString().trim()
    };
  }

  // トリム済みの入力から moment の開始／終了日時を生成する
  private getRangeFromTrimmed(trimmed: {
    date: string;
    from: string;
    to: string;
  }) {
    const start = moment(
      `${trimmed.date} ${trimmed.from}`,
      "YYYY-MM-DD HH:mm",
      true
    );
    const end = moment(
      `${trimmed.date} ${trimmed.to}`,
      "YYYY-MM-DD HH:mm",
      true
    );

    if (!start.isValid() || !end.isValid()) {
      return null;
    }

    return { start, end };
  }

  // 保存処理
  save(): void {
    this.resetRowErrors();

    let hasRowErrors = false;
    const filled: JobSettingSeminarScheduleItem[] = [];

    this.schedule.forEach((setting, index) => {
      const errors = this.getRowErrors(setting);
      this.rowErrors[index] = errors;
      if (errors.length > 0) {
        hasRowErrors = true;
        return;
      }

      if (this.removeEmpty(setting)) {
        filled.push(setting);
      }
    });

    if (hasRowErrors) {
      return;
    }

    // 日本時間で「明日以降」のスケジュールが1件以上あることを必須にする
    const jstTomorrow = moment()
      .utcOffset(9)
      .startOf("day")
      .add(1, "day");

    const hasFuture = filled.some(setting => {
      const entryDate = moment(setting.eventDateAt, "YYYY-MM-DD", true)
        .utcOffset(9)
        .startOf("day");
      return entryDate.isSameOrAfter(jstTomorrow);
    });

    if (!hasFuture) {
      alert(
        "明日以降の開催日が1件以上必要です。空欄もしくは今日のみのスケジュールでは保存できません。"
      );
      return;
    }

    // 保存処理
    const data = filled.map(setting => ({
      id: setting.id || undefined,
      startAt: moment(
        `${setting.eventDateAt} ${setting.eventDateTimeFrom}`,
        "YYYY-MM-DD HH:mm"
      ).toISOString(),
      endAt: moment(
        `${setting.eventDateAt} ${setting.eventDateTimeTo}`,
        "YYYY-MM-DD HH:mm"
      ).toISOString(),
      capacity: setting.capacity
    }));

    this.jobApiService
      .updateJobSettingSeminar(this.jobId, {
        isBasicInfoRequired: this.dynamicFormModel.isBasicInfoRequired ? 1 : 0,
        isAutoConfirm: this.dynamicFormModel.isAutoConfirm ? 1 : 0,
        isResumeRequired: this.dynamicFormModel.isResumeRequired ? 1 : 0,
        publishStatus: this.dynamicFormModel.seminarStatus,
        reservationDeadlineDays: this.dynamicFormModel.reservationDeadlineDays,
        reservationDeadlineTime: moment(
          this.dynamicFormModel.reservationDeadlineTime,
          "HH:mm"
        ).format("HH:mm:ss"),
        schedule: data
      })
      .subscribe(() => {
        this.load();
      });
  }
}
